/**
 * Instrument definitions for sensory-ui.
 *
 * An "instrument" defines HOW a sound is synthesized - the oscillator type,
 * filter characteristics, envelope shape, and timbral qualities.
 *
 * The same tune (musical content) can be played by different instruments
 * to create distinct soundpack characters.
 *
 * Based on the FeelParams system from userinterface.wiki
 */
// ---------------------------------------------------------------------------
// Instrument Types
// ---------------------------------------------------------------------------
/**
 * Simplified instrument config based on FeelParams.
 * These 6 parameters are sufficient to create distinctly different sounds.
 */
export interface InstrumentConfig {
  /** Filter cutoff frequency in Hz */
  filterFreq: number;
  /** Filter Q (resonance) value */
  q: number;
  /** Primary oscillator waveform */
  oscType: "sine" | "square" | "sawtooth" | "triangle";
  /** Decay time multiplier (affects how long sounds ring) */
  decayMult: number;
  /** Gain/volume multiplier */
  gainMult: number;
  /** Pitch multiplier (affects base frequency) */
  pitchMult: number;
}

// ---------------------------------------------------------------------------
// Preset Instruments - 9 distinct feels
// ---------------------------------------------------------------------------









/** Crisp: Sharp, defined, precise - like high-quality headphones */
export const CRISP_INSTRUMENT: InstrumentConfig = {
  filterFreq: 5500,
  q: 4,
  oscType: "triangle",
  decayMult: 0.6,
  gainMult: 1.0,
  pitchMult: 1.1,
};

// ---------------------------------------------------------------------------
// Instrument Presets Map
// ---------------------------------------------------------------------------



// ---------------------------------------------------------------------------
// Synthesis Helpers
// ---------------------------------------------------------------------------

/**
 * Create white/pink/brown noise buffer
 */

/**
 * Apply exponential decay to a noise buffer
 */
export function applyDecayToBuffer(
  buffer: AudioBuffer,
  decayRate: number = 0.3
): void {
  const data = buffer.getChannelData(0);
  const length = data.length;
  for (let i = 0; i < length; i++) {
    data[i] *= Math.exp(-i / (length * decayRate));
  }
}

/**
 * Create oscillator with instrument settings
 */
export function createOscillator(
  ctx: AudioContext,
  freq: number,
  instrument: InstrumentConfig
): OscillatorNode {
  const osc = ctx.createOscillator();
  osc.type = instrument.oscType;
  osc.frequency.value = freq * instrument.pitchMult;
  return osc;
}

/**
 * Create gain with envelope
 */

/**
 * Create filter for noise-based sounds
 */
